/**
 * Barre de mise en forme de l'éditeur (design system, BarreDOutilsEditeur) et sélecteur de
 * surlignage. Un seul arrêt de tabulation, flèches pour passer d'un bouton à l'autre.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { COULEURS_DE_BASE } from '../../contenu/syntaxe';
import { Icone, type NomIcone } from '../composants/Icone';
import { cx } from '../outils';
import type { FormatDEditeur } from './commandes';

interface Outil {
  format: FormatDEditeur;
  libelle: string;
  icone: NomIcone;
  raccourci?: string;
  syntaxe: string;
}

const GROUPES: Outil[][] = [
  [
    { format: 'titre-1', libelle: 'Titre 1', icone: 'heading-1', syntaxe: '# ' },
    { format: 'titre-2', libelle: 'Titre 2', icone: 'heading-2', syntaxe: '## ' },
    { format: 'titre-3', libelle: 'Titre 3', icone: 'heading-3', syntaxe: '### ' },
  ],
  [
    { format: 'gras', libelle: 'Gras', icone: 'bold', raccourci: 'Meta+B', syntaxe: '**texte**' },
    {
      format: 'italique',
      libelle: 'Italique',
      icone: 'italic',
      raccourci: 'Meta+I',
      syntaxe: '*texte*',
    },
    { format: 'barre', libelle: 'Barré', icone: 'strikethrough', syntaxe: '~~texte~~' },
  ],
  [
    { format: 'liste', libelle: 'Liste à tirets', icone: 'list', syntaxe: '- ' },
    {
      format: 'liste-numerotee',
      libelle: 'Liste numérotée',
      icone: 'list-ordered',
      syntaxe: '1. ',
    },
    { format: 'citation', libelle: 'Citation', icone: 'text-quote', syntaxe: '> ' },
  ],
  [
    {
      format: 'lien',
      libelle: 'Lien',
      icone: 'link',
      raccourci: 'Meta+K',
      syntaxe: '[texte](adresse)',
    },
    { format: 'code', libelle: 'Code dans la ligne', icone: 'code', syntaxe: '`code`' },
    { format: 'bloc-de-code', libelle: 'Bloc de code', icone: 'braces', syntaxe: '```' },
    { format: 'formule', libelle: 'Formule', icone: 'sigma', syntaxe: '$formule$' },
    { format: 'image', libelle: 'Image', icone: 'image', syntaxe: '![texte](image)' },
  ],
];

const NOMS_COULEURS: Record<string, string> = {
  violet: 'violet',
  bleu: 'bleu',
  rouge: 'rouge',
  jaune: 'jaune',
  vert: 'vert',
};

/** Fond d'une pastille : jeton de base ou couleur personnelle. */
function styleCouleur(couleur: string) {
  return couleur.startsWith('#')
    ? {
        ['--fc-surlignage' as string]: couleur,
        ['--fc-surlignage-texte' as string]: 'var(--texte)',
      }
    : {
        ['--fc-surlignage' as string]: `var(--surligne-${couleur})`,
        ['--fc-surlignage-texte' as string]: `var(--surligne-${couleur}-texte)`,
      };
}

export interface BarreDOutilsEditeurProps {
  type: 'basique' | 'trous' | 'image' | 'tableau';
  formatsActifs: FormatDEditeur[];
  /** Dernière couleur de surlignage employée. */
  surlignage: string;
  /** Couleur du surlignage au curseur, ou `null`. */
  surlignageCourant: string | null;
  couleursPersonnelles: string[];
  onFormat: (format: FormatDEditeur) => void;
  onSurligner: (couleur: string | 'aucune') => void;
  onAjouterCouleur: (couleur: string) => void;
  collee?: boolean;
  desactivee?: boolean;
}

export function BarreDOutilsEditeur({
  type,
  formatsActifs,
  surlignage,
  surlignageCourant,
  couleursPersonnelles,
  onFormat,
  onSurligner,
  onAjouterCouleur,
  collee = false,
  desactivee = false,
}: BarreDOutilsEditeurProps) {
  const barre = useRef<HTMLDivElement>(null);
  const [actif, setActif] = useState(0);
  const [selecteur, setSelecteur] = useState(false);
  const ancre = useRef<HTMLDivElement>(null);
  const saisieCouleur = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!selecteur) return;
    const exterieur = (e: PointerEvent) => {
      if (!ancre.current?.contains(e.target as Node)) setSelecteur(false);
    };
    document.addEventListener('pointerdown', exterieur, true);
    return () => document.removeEventListener('pointerdown', exterieur, true);
  }, [selecteur]);

  const boutons = () => [
    ...(barre.current?.querySelectorAll<HTMLButtonElement>(
      ':scope > * > button, :scope > button, :scope > * > .fc-popover__ancre > button',
    ) ?? []),
  ];

  function clavier(e: KeyboardEvent<HTMLDivElement>) {
    if (!(e.target instanceof HTMLButtonElement) || e.target.closest('.fc-popover')) return;
    const liste = boutons();
    const i = liste.indexOf(e.target);
    let j = i;
    if (e.key === 'ArrowRight') j = (i + 1) % liste.length;
    else if (e.key === 'ArrowLeft') j = (i - 1 + liste.length) % liste.length;
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = liste.length - 1;
    else return;
    e.preventDefault();
    setActif(j);
    liste[j]?.focus();
  }

  let index = 0;
  const bouton = (outil: Outil) => {
    const i = index++;
    const presse =
      outil.format !== 'image' && outil.format !== 'bloc-de-code'
        ? formatsActifs.includes(outil.format)
        : undefined;
    return (
      <button
        key={outil.format}
        type="button"
        className="fc-bouton fc-bouton--discret fc-bouton--compact fc-bouton--icone"
        aria-label={outil.libelle}
        aria-pressed={presse}
        aria-keyshortcuts={outil.raccourci}
        aria-disabled={desactivee || undefined}
        title={`${outil.libelle}  ${outil.syntaxe}${outil.raccourci ? `  ${outil.raccourci.replace('Meta+', '⌘ ')}` : ''}`}
        tabIndex={i === actif ? 0 : -1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setActif(i);
          if (!desactivee) onFormat(outil.format);
        }}
      >
        <Icone nom={outil.icone} />
      </button>
    );
  };

  // Le surlignage prend la place qui suit Barré dans l'ordre des flèches.
  let indexSurlignage = -1;
  const reserverSurlignage = () => {
    indexSurlignage = index++;
    return null;
  };
  return (
    <div
      ref={barre}
      className={cx('fc-barre-d-outils-editeur', collee && 'fc-barre-d-outils-editeur--collee')}
      role="toolbar"
      aria-label="Mise en forme"
      onKeyDown={clavier}
    >
      {GROUPES.map((groupe, g) => (
        <div key={g} style={{ display: 'contents' }}>
          {g > 0 && <span className="fc-barre-d-outils-editeur__separateur" aria-hidden="true" />}
          <div className="fc-barre-d-outils-editeur__groupe">
            {groupe.map(bouton)}
            {g === 1 && reserverSurlignage()}
            {g === 1 && (
              <div className="fc-popover__ancre" ref={ancre}>
                <button
                  type="button"
                  className="fc-bouton fc-bouton--discret fc-bouton--compact fc-bouton--icone fc-selecteur-de-surlignage__declencheur"
                  aria-label={`Surlignage, ${NOMS_COULEURS[surlignage] ?? 'couleur personnelle'}`}
                  title="Surlignage  ==texte==  (second clic : choisir la couleur)"
                  aria-haspopup="dialog"
                  aria-expanded={selecteur}
                  aria-pressed={formatsActifs.includes('surlignage')}
                  aria-disabled={desactivee || undefined}
                  tabIndex={actif === indexSurlignage ? 0 : -1}
                  style={{
                    ['--fc-surlignage' as string]: styleCouleur(surlignage)['--fc-surlignage'],
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (desactivee) return;
                    if (formatsActifs.includes('surlignage') || selecteur) setSelecteur(!selecteur);
                    else onSurligner(surlignage);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setSelecteur(true);
                    }
                  }}
                >
                  <Icone nom="highlighter" />
                  <span className="fc-selecteur-de-surlignage__temoin" aria-hidden="true" />
                </button>
                {selecteur && (
                  <div
                    className="fc-popover fc-selecteur-de-surlignage"
                    role="dialog"
                    aria-label="Surlignage"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        setSelecteur(false);
                      }
                    }}
                  >
                    <div className="fc-selecteur-de-surlignage__section">
                      <p className="fc-selecteur-de-surlignage__intitule" id="surlignage-base">
                        Couleurs de base
                      </p>
                      <div
                        className="fc-selecteur-de-surlignage__pastilles"
                        role="radiogroup"
                        aria-labelledby="surlignage-base"
                      >
                        {COULEURS_DE_BASE.map((couleur) => (
                          <button
                            key={couleur}
                            type="button"
                            role="radio"
                            className="fc-selecteur-de-surlignage__pastille"
                            aria-checked={surlignageCourant === couleur}
                            aria-label={`Surligner en ${couleur}`}
                            style={styleCouleur(couleur)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onSurligner(couleur)}
                          >
                            <Icone nom="check" />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="fc-selecteur-de-surlignage__section">
                      <p className="fc-selecteur-de-surlignage__intitule" id="surlignage-perso">
                        Vos couleurs
                      </p>
                      <div
                        className="fc-selecteur-de-surlignage__pastilles"
                        role="radiogroup"
                        aria-labelledby="surlignage-perso"
                      >
                        {couleursPersonnelles.map((couleur) => (
                          <button
                            key={couleur}
                            type="button"
                            role="radio"
                            className="fc-selecteur-de-surlignage__pastille"
                            aria-checked={surlignageCourant === couleur}
                            aria-label={`Surligner en ${couleur} (couleur personnelle)`}
                            style={styleCouleur(couleur)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onSurligner(couleur)}
                          >
                            <Icone nom="check" />
                          </button>
                        ))}
                        <button
                          type="button"
                          className="fc-selecteur-de-surlignage__ajouter"
                          aria-label="Ajouter une couleur"
                          title="Ajouter une couleur"
                          onClick={() => saisieCouleur.current?.click()}
                        >
                          <Icone nom="plus" />
                        </button>
                        <input
                          ref={saisieCouleur}
                          type="color"
                          className="fc-sr"
                          tabIndex={-1}
                          aria-hidden="true"
                          defaultValue="#cdb4db"
                          onChange={(e) => onAjouterCouleur(e.target.value.toLowerCase())}
                        />
                      </div>
                    </div>
                    <div className="fc-selecteur-de-surlignage__pied">
                      <button
                        type="button"
                        className="fc-bouton fc-bouton--discret fc-bouton--compact"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          onSurligner('aucune');
                          setSelecteur(false);
                        }}
                      >
                        <Icone nom="eraser" />
                        Retirer le surlignage
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
      {type === 'trous' && (
        <button
          type="button"
          className="fc-bouton fc-bouton--secondaire fc-bouton--compact fc-barre-d-outils-editeur__fin"
          aria-keyshortcuts="Meta+Shift+C"
          title="Trou  {{réponse}}  ⌘ ⇧ C"
          aria-pressed={formatsActifs.includes('trou')}
          aria-disabled={desactivee || undefined}
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => !desactivee && onFormat('trou')}
        >
          <Icone nom="square-dashed" />
          Trou
        </button>
      )}
    </div>
  );
}
